import React from 'react';
import { useAccessibility } from '../../context/AccessibilityContext';
import { Typography } from './Typography';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import MotionPhotosOffIcon from '@mui/icons-material/MotionPhotosOff';
import ContrastIcon from '@mui/icons-material/Contrast';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description: string;
  icon: React.ReactNode;
  disabled?: boolean;
}

function ToggleSwitch({ checked, onChange, label, description, icon, disabled = false }: ToggleSwitchProps) {
  return (
    <div className="flex items-start space-x-4 p-4 rounded-lg bg-surface-raised/50 hover:bg-surface-raised transition-colors">
      <div className="flex-shrink-0 text-naia-pink mt-1">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <Typography variant="subtitle2" weight="medium" className="text-text-primary">
              {label}
            </Typography>
            <Typography variant="body2" color="muted" className="mt-1">
              {description}
            </Typography>
          </div>
          <label className="relative inline-flex items-center cursor-pointer ml-4">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={checked}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
            />
            <div className={`
              w-11 h-6 rounded-full peer transition-colors duration-200
              ${checked 
                ? 'bg-naia-pink' 
                : 'bg-surface-border'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-naia-pink/50
              after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
              after:bg-pearl-white after:rounded-full after:h-5 after:w-5 
              after:transition-all after:duration-200
              ${checked ? 'after:translate-x-full' : ''}
            `} />
          </label>
        </div>
      </div>
    </div>
  );
}

interface AccessibilitySettingsProps {
  className?: string;
  compact?: boolean;
}

export function AccessibilitySettings({ className = '', compact = false }: AccessibilitySettingsProps) {
  const {
    state,
    toggleHighContrast,
    toggleReducedMotion,
    toggleLargeText,
    toggleScreenReaderMode,
    resetToDefaults,
  } = useAccessibility();

  const settings = [
    {
      key: 'highContrast',
      checked: state.highContrast,
      onChange: toggleHighContrast,
      label: 'High Contrast Mode',
      description: 'Increases contrast for better visibility and readability',
      icon: <ContrastIcon />,
    },
    {
      key: 'reducedMotion',
      checked: state.reducedMotion,
      onChange: toggleReducedMotion,
      label: 'Reduced Motion',
      description: 'Reduces animations and motion effects for better accessibility',
      icon: <MotionPhotosOffIcon />,
    },
    {
      key: 'largeText',
      checked: state.largeText,
      onChange: toggleLargeText,
      label: 'Large Text',
      description: 'Increases text size throughout the application',
      icon: <TextFieldsIcon />,
    },
    {
      key: 'screenReaderOptimized',
      checked: state.screenReaderOptimized,
      onChange: toggleScreenReaderMode,
      label: 'Screen Reader Optimization',
      description: 'Optimizes the interface for screen readers and assistive technologies',
      icon: <RecordVoiceOverIcon />,
    },
  ];

  const infoSettings = [
    {
      key: 'keyboardNavigation',
      checked: state.keyboardNavigation,
      label: 'Keyboard Navigation',
      description: 'Navigate using keyboard shortcuts (automatically detected)',
      icon: <KeyboardIcon />,
      disabled: true,
    },
    {
      key: 'showFocusRings',
      checked: state.showFocusRings,
      label: 'Focus Indicators',
      description: 'Shows focus rings when navigating with keyboard',
      icon: <VisibilityIcon />,
      disabled: true,
    },
  ];

  if (compact) {
    return (
      <div className={`space-y-3 ${className}`}>
        <Typography variant="h6" weight="semibold" className="text-text-primary">
          Accessibility
        </Typography>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {settings.slice(0, 4).map((setting) => (
            <div key={setting.key} className="flex items-center justify-between p-3 bg-surface-raised rounded-lg">
              <div className="flex items-center space-x-2">
                <span className="text-naia-pink text-sm">{setting.icon}</span>
                <span className="text-sm font-medium text-text-primary">{setting.label}</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={setting.checked}
                  onChange={(e) => setting.onChange()}
                />
                <div className={`
                  w-9 h-5 rounded-full peer transition-colors duration-200
                  ${setting.checked ? 'bg-naia-pink' : 'bg-surface-border'}
                  peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-naia-pink/50
                  after:content-[''] after:absolute after:top-[1px] after:left-[1px] 
                  after:bg-pearl-white after:rounded-full after:h-4 after:w-4 
                  after:transition-all after:duration-200
                  ${setting.checked ? 'after:translate-x-4' : ''}
                `} />
              </label>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <AccessibilityIcon className="text-naia-pink text-2xl" />
          <Typography variant="h4" weight="bold" className="text-text-primary">
            Accessibility Settings
          </Typography>
        </div>
        <button
          onClick={resetToDefaults}
          className="px-4 py-2 text-sm bg-surface-raised hover:bg-surface-high text-text-secondary hover:text-text-primary border border-surface-border rounded-lg transition-all duration-200"
        >
          Reset to Defaults
        </button>
      </div>

      <Typography variant="body1" color="secondary" reading className="max-w-2xl">
        Customize your experience with accessibility features designed to make Naia more comfortable and usable for everyone.
      </Typography>

      {/* Main accessibility settings */}
      <div className="space-y-4">
        <Typography variant="h6" weight="semibold" className="text-text-primary">
          Visual & Motion
        </Typography>
        <div className="space-y-3">
          {settings.map((setting) => (
            <ToggleSwitch
              key={setting.key}
              checked={setting.checked}
              onChange={setting.onChange}
              label={setting.label}
              description={setting.description}
              icon={setting.icon}
            />
          ))}
        </div>
      </div>

      {/* Information-only settings */}
      <div className="space-y-4">
        <Typography variant="h6" weight="semibold" className="text-text-primary">
          Navigation & Input
        </Typography>
        <div className="space-y-3">
          {infoSettings.map((setting) => (
            <ToggleSwitch
              key={setting.key}
              checked={setting.checked}
              onChange={() => {}} // No-op for disabled settings
              label={setting.label}
              description={setting.description}
              icon={setting.icon}
              disabled={setting.disabled}
            />
          ))}
        </div>
      </div>

      {/* Keyboard shortcuts info */}
      <div className="p-4 bg-surface-raised/30 rounded-lg border border-surface-border">
        <Typography variant="h6" weight="semibold" className="text-text-primary mb-3">
          Keyboard Shortcuts
        </Typography>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div className="flex justify-between">
            <span className="text-text-secondary">Search</span>
            <kbd className="px-2 py-1 bg-surface-high rounded text-text-primary font-mono text-xs">
              Cmd+K
            </kbd>
          </div>
          <div className="flex justify-between">
            <span className="text-text-secondary">Play/Pause</span>
            <kbd className="px-2 py-1 bg-surface-high rounded text-text-primary font-mono text-xs">
              Space
            </kbd>
          </div>
          <div className="flex justify-between">
            <span className="text-text-secondary">Previous Track</span>
            <kbd className="px-2 py-1 bg-surface-high rounded text-text-primary font-mono text-xs">
              Alt+←
            </kbd>
          </div>
          <div className="flex justify-between">
            <span className="text-text-secondary">Next Track</span>
            <kbd className="px-2 py-1 bg-surface-high rounded text-text-primary font-mono text-xs">
              Alt+→
            </kbd>
          </div>
          <div className="flex justify-between">
            <span className="text-text-secondary">Close Modal</span>
            <kbd className="px-2 py-1 bg-surface-high rounded text-text-primary font-mono text-xs">
              Escape
            </kbd>
          </div>
        </div>
      </div>

      {/* Status indicators */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-surface-raised/30 rounded-lg text-center">
          <Typography variant="subtitle2" weight="medium" className="text-text-primary">
            Keyboard Navigation
          </Typography>
          <div className={`mt-2 w-3 h-3 rounded-full mx-auto ${
            state.keyboardNavigation ? 'bg-success' : 'bg-surface-border'
          }`} />
          <Typography variant="caption" color="muted" className="mt-1">
            {state.keyboardNavigation ? 'Active' : 'Inactive'}
          </Typography>
        </div>
        
        <div className="p-4 bg-surface-raised/30 rounded-lg text-center">
          <Typography variant="subtitle2" weight="medium" className="text-text-primary">
            Screen Reader
          </Typography>
          <div className={`mt-2 w-3 h-3 rounded-full mx-auto ${
            state.screenReaderOptimized ? 'bg-success' : 'bg-surface-border'
          }`} />
          <Typography variant="caption" color="muted" className="mt-1">
            {state.screenReaderOptimized ? 'Optimized' : 'Standard'}
          </Typography>
        </div>
        
        <div className="p-4 bg-surface-raised/30 rounded-lg text-center">
          <Typography variant="subtitle2" weight="medium" className="text-text-primary">
            Announcements
          </Typography>
          <div className={`mt-2 w-3 h-3 rounded-full mx-auto ${
            state.announcements ? 'bg-success' : 'bg-surface-border'
          }`} />
          <Typography variant="caption" color="muted" className="mt-1">
            {state.announcements ? 'Enabled' : 'Disabled'}
          </Typography>
        </div>
      </div>
    </div>
  );
} 